import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { getToken, setToken, clearToken } from "../lib/tokenStore";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    const token = getToken();
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { clearToken(); })
      .finally(() => setLoading(false));
  }, []);

  // Auto-refresh user data when app regains focus (e.g. user returns to the
  // iOS WebView after paying on Safari/thaqh.com). Without this, the cached
  // user object still shows subscription_plan="free" even after a successful
  // payment, causing AI evaluations to be blocked with "Subscribe required".
  useEffect(() => {
    const handleFocus = () => {
      const token = getToken();
      if (!token) return;
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") handleFocus();
    });
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // Used by alternative login flows (e.g. WhatsApp OTP) that already obtained
  // a JWT + user from the server. Bypasses email/password.
  const loginWithToken = useCallback(({ token, user: u }) => {
    setToken(token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (err) {
      // Token invalid/expired — log and ignore. Caller will see user=null.
      if (process.env.NODE_ENV !== "production") {
        console.warn("auth.refresh failed:", err?.response?.status || err?.message);
      }
    }
  }, []);

  // Memoise context value — prevents every consumer re-rendering on every parent render.
  const value = useMemo(
    () => ({ user, loading, login, register, loginWithToken, logout, refresh }),
    [user, loading, login, register, loginWithToken, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
